import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { whitePaper } from '../content/blog/whitepaper';
import './WhitePaperScreen.css';

const WhitePaperScreen: React.FC = () => {
  const { theme } = useTheme();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Redirect non-admin users
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/blog');
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) {
    return (
      <div className="white-paper-screen">
        <div className="container">
          <h1>Access Denied</h1>
          <p>This content is only available to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="white-paper-screen" style={{ backgroundColor: theme.colors.background }}>
      <div className="container">
        <div className="white-paper-header">
          <button
            onClick={() => navigate('/blog')}
            className="back-button"
            style={{
              color: theme.colors.primary,
              borderColor: theme.colors.primary
            }}
          >
            ← Back to Blog
          </button>
          <div className="admin-badge" style={{ backgroundColor: theme.colors.primary }}>
            🔒 Admin Only
          </div>
        </div>

        <article className="white-paper-content" style={{ backgroundColor: theme.colors.surface }}>
          <header className="white-paper-title-section">
            <h1 style={{ color: theme.colors.text }}>{whitePaper.title}</h1>
            <h2 className="white-paper-subtitle" style={{ color: theme.colors.textSecondary }}>
              {whitePaper.subtitle}
            </h2>
            <div className="white-paper-meta">
              <span style={{ color: theme.colors.textSecondary }}>
                Author: {whitePaper.author}
              </span>
              <span style={{ color: theme.colors.textSecondary }}>
                Date: {new Date(whitePaper.date).toLocaleDateString()}
              </span>
              <span style={{ color: theme.colors.textSecondary }}>
                Version: {whitePaper.version}
              </span>
            </div>
          </header>

          <div className="white-paper-body">
            {whitePaper.sections.map((section, index) => (
              <section key={section.id} className="white-paper-section">
                <h2 
                  id={section.id}
                  className="section-title"
                  style={{ color: theme.colors.text }}
                >
                  {index + 1}. {section.title}
                </h2>
                <div 
                  className="section-content"
                  style={{ color: theme.colors.textSecondary }}
                >
                  {section.content.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </div>

                {section.subsections && section.subsections.map((subsection) => (
                  <div key={subsection.id} className="subsection">
                    <h3 
                      id={subsection.id}
                      className="subsection-title"
                      style={{ color: theme.colors.text }}
                    >
                      {subsection.title}
                    </h3>
                    <div 
                      className="subsection-content"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      {subsection.content.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {index > 0 && <br />}
                          {line}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>

          <footer className="white-paper-footer">
            <div className="white-paper-tags">
              <h4 style={{ color: theme.colors.text }}>Tags:</h4>
              <div className="tags-list">
                {whitePaper.metadata.tags.map((tag) => (
                  <span
                    key={tag}
                    className="white-paper-tag"
                    style={{
                      backgroundColor: theme.colors.primary + '20',
                      color: theme.colors.primary
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="white-paper-info">
              <p style={{ color: theme.colors.textSecondary }}>
                Word Count: {whitePaper.metadata.wordCount}
              </p>
              <p style={{ color: theme.colors.textSecondary }}>
                Last Updated: {new Date(whitePaper.metadata.lastUpdated).toLocaleDateString()}
              </p>
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
};

export default WhitePaperScreen;
